import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { openSnackbarMain } from 'components/Shared/openSnackbarMain';
import { GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { deleteMenuAberto, deleteTelaAberta } from '../../../../services/authentication/auth';
import { useMenuSidebar } from '../../../../contexts/MenuSidebarContext';
import {
  atualizarEtapaCessao,
  getListaTitulosCessao,
  getValorLiberadoCapitalFlow,
  getValorSolicitado,
  iniciarCessaoManual,
  iniciarCessaoSugestao,
  validaTitulosCedidos
} from '../../../../api/capitalFlow';
import { formatarDocumento } from '../../../../components/Utils/documentoAlfanumerico';

const useNovaCessaoHook = ({ setCarregarTela }) => {
  const { esconderMenuPrincipal } = useMenuSidebar();
  const [valorDisponivel, setValorDisponivel] = useState(0);
  const [loadValorDisponivel, setLoadValorDisponivel] = useState(false);
  const [rows, setRows] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });
  const [filtrosAtuais, setFiltrosAtuais] = useState({});
  const [mostrarSimulacao, setMostrarSimulacao] = useState(false);
  const [simularTaxas, setSimularTaxas] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingProsseguir, setLoadingProsseguir] = useState(false);
  const [contadorFiltros, setContadorFiltros] = useState(0);
  const [selecionarManualmente, setSelecionarManualmente] = useState(false);
  const [selectedRowsMap, setSelectedRowsMap] = useState({});
  const [rowCount, setRowCount] = useState(0);
  const [openModalCancelar, setOpenModalCancelar] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  const [buscarSimulacaoResumo, setBuscarSimulacaoResumo] = useState(0);
  const [liberarBtnProsseguir, setLiberarBtnProsseguir] = useState(false);
  const [openModalTitulosCedidos, setOpenModalTitulosCedidos] = useState(false);
  const { state } = useLocation();
  const navigate = useNavigate();
  const inputsRef = useRef();

  useEffect(() => {
    buscaValorLiberado();
    verificaBuscaDadosCessao();
  }, []);

  const verificaBuscaDadosCessao = () => {
    if (state?.buscarDados) {
      setMostrarSimulacao(true);
      buscaValorSolicitado();
    }
  };

  const buscaValorSolicitado = () => {
    getValorSolicitado()
      .then(response => {
        const tipoOrigem = response.data.ORIGEM ?? null;
        const controleSimulacao = tipoOrigem === 'MANUAL';
        setSimularTaxas(controleSimulacao);

        if (!controleSimulacao) {
          setTimeout(() => {
            buscarAtualizarSimulacao();
          }, 8000);
        } else {
          buscarAtualizarSimulacao();
        }

        const valorInformado = response.data.VALORSOLICITADO ?? null;
        const observacaoInformado = response.data.OBSERVACOES ?? null;
        const { valorObservacao } = inputsRef.current.getValues();
        const valorEscolhido = inputsRef.current.getValorEscolhido();

        if (!valorObservacao) {
          inputsRef.current.setValorObservacao(observacaoInformado);
        }

        if (!valorEscolhido) {
          inputsRef.current.setValorEscolhido(valorInformado);
        }
      })
      .catch(err => {
        openSnackbarMain('Ocorreu um erro ao buscar o valor liberado', 'error');
        console.error('Erro ao buscar o valor liberado:', err);
      });
  };

  const irParaTelaUpload = () => {
    setLoadingProsseguir(true);
    const novaEtapa = 'UploadCessao';
    atualizarEtapaCessao({ etapa: novaEtapa })
      .then(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setCarregarTela(novaEtapa);
      })
      .catch(() => {
        openSnackbarMain('Ocorreu um erro ao atualizar a etapa da cessão', 'error');
      })
      .finally(() => setLoadingProsseguir(false));
  };

  const verificaTitulosCedidos = () => {
    setLoadingProsseguir(true);
    validaTitulosCedidos()
      .then(response => {
        if (response.data?.existe_conflito) {
          setOpenModalTitulosCedidos(true);
          setLoadingProsseguir(false);
          liberarSimulacao();
        } else {
          irParaTelaUpload();
        }
      })
      .catch(() => {
        setLoadingProsseguir(false);
        openSnackbarMain('Ocorreu um erro ao validar os títulos cedidos', 'error');
      });
  };

  const carregarTitulos = (model = paginationModel, novosFiltros = null, newSortModel = sortModel) => {
    const filtrosBase = novosFiltros ?? filtrosAtuais;
    const filtrosLimpos = cleanFilters(filtrosBase);
    const listaAtual = rows;
    const sort = newSortModel?.[0];
    setRows([]);
    // guarda filtros atuais pra próxima paginação
    setFiltrosAtuais(filtrosLimpos);

    const { page, pageSize } = model;

    const params = {
      ...filtrosLimpos,
      page: page,
      per_page: pageSize,
      order_by: sort?.sort ?? null,
      column_filter: sort?.field ?? null
    };

    setLoading(true);
    getListaTitulosCessao(params)
      .then(response => {
        const { data } = response.data;
        setRows(data ?? []);
        setRowCount(response.data.meta.total);
      })
      .catch(error => {
        setRows(listaAtual);
        openSnackbarMain('Ocorreu um erro ao buscar lista de títulos', 'error');
        console.error('Erro ao buscar lista de títulos:', error);
      })
      .finally(() => setLoading(false));
  };

  const formataValor = valor =>
    parseFloat(valor).toLocaleString('pt-br', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

  const buscaValorLiberado = () => {
    setLoadValorDisponivel(true);
    getValorLiberadoCapitalFlow()
      .then(response => {
        setValorDisponivel(formataValor(response.data.limite_disponivel ?? 0));
      })
      .catch(err => {
        openSnackbarMain('Ocorreu um erro ao buscar o valor liberado', 'error');
        console.error('Erro ao buscar o valor liberado:', err);
      })
      .finally(() => setLoadValorDisponivel(false));
  };

  const irParaHome = () => {
    deleteMenuAberto();
    deleteTelaAberta();
    esconderMenuPrincipal();
    navigate('/home');
  };

  const irParaSugestao = tipo => {
    const { valorObservacao } = inputsRef.current.getValues();
    const valorEscolhidoNumber = inputsRef.current.getValorEscolhido();

    const payload = {
      VALORSOLICITADO: valorEscolhidoNumber,
      OBSERVACOES: valorObservacao,
      ETAPA: 'NovaCessao'
    };

    if (tipo === 'sugestao') {
      setSimularTaxas(false);
      if (!inputsRef.current.validarValorEscolhido()) {
        requestAnimationFrame(() => {
          document.querySelector('.input-valor-sessao input')?.focus();
        });
        return false;
      }

      controleNavegacaoBuscarDados(true);
      setMostrarSimulacao(true);

      iniciarCessaoSugestao(payload)
        .then(() => {
          buscarAtualizarSimulacao();
        })
        .catch(error => {
          openSnackbarMain('Ocorreu um erro ao incluir nova cessão sugerida', 'error');
          console.error('Erro ao incluir nova cessão sugerida:', error);
          controleNavegacaoBuscarDados(false);
          setMostrarSimulacao(false);
        });
    } else {
      setLoadingProsseguir(true);
      payload.TITULOS = Object.values(selectedRowsMap);
      payload.ORIGEM = 'MANUAL';
      controleNavegacaoBuscarDados(true);
      iniciarCessaoManual(payload)
        .then(() => {
          window.scrollTo({ top: 400, behavior: 'smooth' });
          setMostrarSimulacao(true);
          buscaValorSolicitado();
        })
        .catch(error => {
          openSnackbarMain('Ocorreu um erro ao incluir nova cessão manual', 'error');
          console.error('Erro ao incluir nova cessão manual:', error);
        })
        .finally(() => setLoadingProsseguir(false));
    }
  };

  const buscarAtualizarSimulacao = () => {
    setBuscarSimulacaoResumo(prev => ++prev);
  };

  const controleNavegacaoBuscarDados = tipo => {
    navigate('/capital-flow/nova-cessao', {
      state: { ...state, buscarDados: tipo }
    });
  };

  const toolbarTabela = () => (
    <GridToolbarContainer className="tabela-toolbar">
      <div className="toolbar-spacer" />
      <GridToolbarQuickFilter className="search-grid" placeholder="Procurar..." debounceMs={500} />
    </GridToolbarContainer>
  );

  const parseISODateLocal = s => {
    if (!s) return null;
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const d = new Date(+m[1], +m[2] - 1, +m[3], 0, 0, 0, 0);
      return isNaN(d) ? null : d;
    }
    const d = new Date(s);
    return isNaN(d) ? null : d;
  };

  const isBlank = v =>
    v === null ||
    v === undefined ||
    (typeof v === 'string' && v.trim() === '') ||
    (Array.isArray(v) && v.length === 0) ||
    (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

  const handlePaginationModelChange = model => {
    setPaginationModel(model);
    carregarTitulos(model, filtrosAtuais, sortModel);
  };

  const handleSortModelChange = model => {
    setSortModel(model);
    carregarTitulos(paginationModel, filtrosAtuais, model);
  };

  const cleanFilters = (obj = {}) =>
    Object.fromEntries(Object.entries(obj).filter(([_, v]) => !isBlank(v)));

  useEffect(() => {
    if (selecionarManualmente) {
      carregarTitulos(paginationModel, {});
    }
  }, [selecionarManualmente]);

  const handleFiltrar = filtros => {
    const filtrosLimpos = cleanFilters(filtros);
    const newModel = {
      ...paginationModel,
      page: 0
    };

    setPaginationModel(newModel);
    setContadorFiltros(verificaContaFiltros(filtros));
    carregarTitulos(newModel, filtrosLimpos);
  };

  const verificaContaFiltros = filtros => {
    const on = v => (typeof v === 'string' ? v.trim() !== '' : !!v);
    let n = 0;
    if (on(filtros.nome_cedente)) n++;
    if (on(filtros.cpfcnpj_cedente)) n++;
    if (on(filtros.nome_sacado)) n++;
    if (on(filtros.cpfcnpj_sacado)) n++;
    if (on(filtros.tipo_operacao)) n++;
    if (on(filtros.tipo_documento)) n++;
    if (on(filtros.numero_documento)) n++;
    if (on(filtros.bordero)) n++;
    if (filtros.data_aceite_de || filtros.data_aceite_ate) n++;
    if (filtros.venc_ate || filtros.venc_de) n++;
    return n;
  };

  const fmtBRDate = iso => {
    const d = parseISODateLocal(iso);
    return d ? d.toLocaleDateString('pt-BR') : '';
  };

  const columns = [
    {
      field: 'NOMECEDENTE',
      headerName: 'Cedente',
      flex: 1.5,
      minWidth: 250,
      valueGetter: ({ row }) => `${formatarDocumento(row.CPFCNPJCEDENTE)} - ${row.NOMECEDENTE}`
    },
    {
      field: 'NOMESACADO',
      headerName: 'Sacado',
      flex: 1.5,
      minWidth: 250,
      valueGetter: ({ row }) => `${formatarDocumento(row.CPFCNPJSACADO)} - ${row.NOMESACADO}`
    },
    { field: 'NF', headerName: 'NF', flex: 0.8, minWidth: 110 },
    { field: 'NUMEROTITULO', headerName: 'Nº Doc.', flex: 0.8, minWidth: 110 },
    { field: 'TTO', headerName: 'Tipo Op.', flex: 0.8, minWidth: 110 },
    {
      field: 'VENCIMENTO',
      headerName: 'Vencimento',
      flex: 1.0,
      minWidth: 90,
      valueFormatter: ({ value }) => fmtBRDate(value)
    },
    {
      field: 'VALOR',
      headerName: 'Valor (R$)',
      flex: 1.0,
      minWidth: 120,
      valueFormatter: ({ value }) => formataValor(value)
    }
  ];

  const liberarSimulacao = () => {
    setMostrarSimulacao(false);
    setBuscarSimulacaoResumo(0);
    setLiberarBtnProsseguir(false);
    setSimularTaxas(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (selecionarManualmente) {
      setTimeout(() => {
        window.scrollTo({ top: 450, behavior: 'smooth' });
      }, 350);
    }
  }, [selecionarManualmente]);

  const handleRowSelectionModelChange = newRowSelectionModel => {
    const consistentIds = newRowSelectionModel.map(String);
    const currentPageRowIds = rows.map(row => String(row.id));
    const newSelectedRowsMap = { ...selectedRowsMap };

    for (const rowId of currentPageRowIds) {
      const isRowSelected = consistentIds.includes(rowId);
      const currentRow = rows.find(row => String(row.id) === rowId);

      if (isRowSelected) {
        newSelectedRowsMap[rowId] = currentRow;
      } else {
        delete newSelectedRowsMap[rowId];
      }
    }

    setSelectedRowsMap(newSelectedRowsMap);
  };

  const rowSelectionModelFiltered = rows
    .map(row => String(row.id))
    .filter(id => selectedRowsMap[id] !== undefined);

  const onCloseModalCancelar = () => {
    setOpenModalCancelar(false);
  };

  const onCloseModalTitulosCedidos = () => {
    setOpenModalTitulosCedidos(false);
  };

  return {
    setMostrarSimulacao,
    loadValorDisponivel,
    loadingProsseguir,
    irParaTelaUpload,
    liberarSimulacao,
    mostrarSimulacao,
    valorDisponivel,
    contadorFiltros,
    irParaSugestao,
    toolbarTabela,
    handleFiltrar,
    simularTaxas,
    irParaHome,
    inputsRef,
    columns,
    rows,
    loading,
    paginationModel,
    handlePaginationModelChange,
    setFiltrosAtuais,
    filtrosAtuais,
    rowCount,
    selecionarManualmente,
    setSelecionarManualmente,
    handleRowSelectionModelChange,
    rowSelectionModelFiltered,
    selectedRowsMap,
    openModalCancelar,
    setOpenModalCancelar,
    onCloseModalCancelar,
    handleSortModelChange,
    sortModel,
    buscarSimulacaoResumo,
    buscarAtualizarSimulacao,
    liberarBtnProsseguir,
    setLiberarBtnProsseguir,
    openModalTitulosCedidos,
    onCloseModalTitulosCedidos,
    verificaTitulosCedidos
  };
};

export default useNovaCessaoHook;
